import { Button } from '@mui/material';
import TextField from '@mui/material/TextField';
import { UnitsWeight } from 'harmony-constants';
import { L } from 'harmony-language';
import React from 'react';
import { useUser } from '../../../api/queries/use-user';
import { Contact, NOTIFICATION_TYPES, NotificationType, User } from '../../../types';
import { CheckboxEnabled } from '../../shared/inputs/checkbox-enabled';
import { PhoneInput } from '../../shared/inputs/phone-input';
import { SelectLanguage } from '../../shared/inputs/select-language';
import { SelectTimezone } from '../../shared/inputs/select-timezone';
import { SelectUnits } from '../../shared/inputs/select-units';
import { SelectUnitsWeight } from '../../shared/inputs/select-units-weight';
import { EditCreateUserItem } from '../users/edit-create-user/edit-create-user';

interface ConditionalFormWrapperProps {
    condition: boolean;
    wrapper: (c: any) => React.ReactNode;
    children: any;
}

const ConditionalFormWrapper = (props: ConditionalFormWrapperProps) => {
    const { condition, wrapper, children } = props;

    return (condition ? wrapper(children) : children);
}

const createNewContact = (self: User) => {
    return {
        name: '',
        language: self?.organization?.language || 'en-US',
        timezone: self?.organization?.timezone || 'America/Chicago',
        units: self?.organization?.units || 'Imperial',
        unitsWeight: self?.organization?.unitsWeight || UnitsWeight.Pound,
        enabled: true,
        contactNotificationMethods: [],
    };
};

type EditCreateContactProps = {
    contact: Contact;
    onContactChanged?: (contact: Contact) => void;
    onSubmit?: (contact: Contact) => void;
};

export const EditCreateContact = (props: EditCreateContactProps) => {
    const { contact, onContactChanged, onSubmit } = props;
    const { user: self } = useUser();
    const [editContact, setEditContact] = React.useState<Contact>(contact || createNewContact(self));

    //current business assumption only 1 of each email / phone.  Though database model supports multiple if needed in future.
    const notificationTypesSplitter = (notificationType: NotificationType) => {
        const existing = editContact.contactNotificationMethods.find(x => x.notificationType === notificationType);
        const others = editContact.contactNotificationMethods.filter(x => x.notificationType !== notificationType);
        return { existing, others };
    };

    const update = (updatedContact: Contact) => {
        setEditContact(updatedContact);
        onContactChanged && onContactChanged(updatedContact);
    };

    const updateContactNotificationMethod = (notificationType: NotificationType, value: string) => {
        const { existing, others } = notificationTypesSplitter(notificationType);

        if (existing) {
            update({ ...editContact, contactNotificationMethods: [ ...others, { ...existing, destination: value}]});
        } else {
            update({ ...editContact, contactNotificationMethods: [ ...others, { 
                ...(contact?.id ? { contactId: contact.id } : {}),
                notificationType: notificationType,
                destination: value,
            }]});
        }
    };

    const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        onSubmit && onSubmit(editContact)
    };

    // <EditCreateUserItem> only has an effect when within the <EditCreateUser> <GridContainer> container component
    return (
        <ConditionalFormWrapper
            condition={onSubmit ? true : false}
            wrapper={(children: any) => <form id='edit-create-contact-form' name='edit-create-contact-form' onSubmit={submitForm}>{children}</form>}
        >
            <EditCreateUserItem>
                <TextField
                    variant='standard'
                    label={L.name()}
                    fullWidth
                    required={true}
                    value={editContact?.name}
                    onChange={(e) => {
                        update({ ...editContact, name: e.target.value });
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <TextField
                    variant='standard'
                    label={L.email()}
                    fullWidth
                    value={notificationTypesSplitter(NOTIFICATION_TYPES.Email).existing?.destination || ''}
                    onChange={(e) => {
                        updateContactNotificationMethod(NOTIFICATION_TYPES.Email, e.target.value);
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <PhoneInput
                    phone={notificationTypesSplitter(NOTIFICATION_TYPES.Text).existing?.destination}
                    onChange={(phone: string) => {
                        updateContactNotificationMethod(NOTIFICATION_TYPES.Text, phone);
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <SelectLanguage
                    language={editContact?.language}
                    onChange={(e) => {
                        update({ ...editContact, language: e });
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <SelectTimezone
                    timezone={editContact?.timezone}
                    onChange={(e) => {
                        update({ ...editContact, timezone: e });
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <SelectUnits
                    units={editContact?.units}
                    onChange={(e) => {
                        update({ ...editContact, units: e });
                    }}
                />
            </EditCreateUserItem>
            <EditCreateUserItem>
                <SelectUnitsWeight
                    unitsWeight={editContact.unitsWeight}
                    onChange={(e: UnitsWeight) => {
                        update({ ...editContact, unitsWeight: e });
                    }}
                />
            </EditCreateUserItem>
            {onSubmit &&
                <EditCreateUserItem>
                    <CheckboxEnabled
                        checked={editContact.enabled}
                        onChange={() => update({ ...editContact, enabled: !editContact.enabled })}
                    />
                </EditCreateUserItem>
            }
            {onSubmit &&
                <EditCreateUserItem>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '1rem'
                    }}>
                        <Button type='submit'
                            title={L.save()}
                            variant='contained'
                            color='primary'>{editContact.id ? L.updateContact() : L.createContact()}
                        </Button>
                    </div>
                </EditCreateUserItem>
            }
        </ConditionalFormWrapper>
    );
}
